import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import {
  Globe, Activity, TrendingUp, Shield, AlertTriangle, Eye, Clock, Target,
  Zap, Database, RefreshCw, ChevronRight, ExternalLink, MapPin, Loader2,
  BarChart3, PieChart as PieIcon, Bug, Skull, FileWarning, Radio, Wifi, WifiOff, Radar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AnimatedBackground, GlowOrbs } from '@/components/AnimatedBackground';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, AreaChart, Area, ComposedChart, Line, Legend
} from 'recharts';

// World Map Component using SVG
const WorldMapSVG = ({ attackOrigins, attackTargets, isDarkMode }) => {
  // Simplified world map coordinates
  const mapWidth = 800;
  const mapHeight = 400;
  
  // Convert lat/lng to SVG coordinates
  const latLngToXY = (lat, lng) => {
    const x = ((lng + 180) / 360) * mapWidth;
    const y = ((90 - lat) / 180) * mapHeight;
    return { x, y };
  };
  
  return (
    <svg viewBox={`0 0 ${mapWidth} ${mapHeight}`} className="w-full h-full">
      {/* Background */}
      <rect width={mapWidth} height={mapHeight} fill={isDarkMode ? '#1a1a2e' : '#e8f4fc'} />
      
      {/* Simple continent outlines */}
      <g fill={isDarkMode ? '#2a2a4e' : '#b8d4e8'} stroke={isDarkMode ? '#3a3a6e' : '#7eb8da'} strokeWidth="0.5">
        {/* North America */}
        <ellipse cx="180" cy="130" rx="100" ry="60" />
        {/* South America */}
        <ellipse cx="230" cy="280" rx="50" ry="80" />
        {/* Europe */}
        <ellipse cx="420" cy="110" rx="50" ry="40" />
        {/* Africa */}
        <ellipse cx="440" cy="230" rx="60" ry="80" />
        {/* Asia */}
        <ellipse cx="600" cy="140" rx="120" ry="80" />
        {/* Australia */}
        <ellipse cx="680" cy="310" rx="40" ry="30" />
      </g>
      
      {/* Connection lines from origins to targets */}
      <g stroke={isDarkMode ? 'rgba(239, 68, 68, 0.3)' : 'rgba(220, 38, 38, 0.2)'} strokeWidth="1" fill="none">
        {attackOrigins?.slice(0, 5).map((origin, i) => {
          const originPos = latLngToXY(origin.lat, origin.lng);
          return attackTargets?.slice(0, 3).map((target, j) => {
            const targetPos = latLngToXY(target.lat, target.lng);
            const midX = (originPos.x + targetPos.x) / 2;
            const midY = Math.min(originPos.y, targetPos.y) - 50;
            return (
              <path
                key={`${i}-${j}`}
                d={`M ${originPos.x} ${originPos.y} Q ${midX} ${midY} ${targetPos.x} ${targetPos.y}`}
                className="animate-pulse"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            );
          });
        })}
      </g>
      
      {/* Attack origins (red) */}
      {attackOrigins?.map((origin, i) => {
        const { x, y } = latLngToXY(origin.lat, origin.lng);
        const size = Math.min(Math.max(origin.count / 5, 4), 12);
        return (
          <g key={`origin-${i}`}>
            {/* Pulse effect */}
            <circle
              cx={x}
              cy={y}
              r={size + 8}
              fill="none"
              stroke="rgba(239, 68, 68, 0.5)"
              strokeWidth="2"
              className="animate-ping"
              style={{ animationDuration: '2s', animationDelay: `${i * 0.3}s` }}
            />
            <circle
              cx={x}
              cy={y}
              r={size}
              fill="#ef4444"
              stroke="#fff"
              strokeWidth="1"
              className="cursor-pointer"
            >
              <title>{origin.name}: {origin.count} attacks</title>
            </circle>
          </g>
        );
      })}
      
      {/* Attack targets (blue) */}
      {attackTargets?.map((target, i) => {
        const { x, y } = latLngToXY(target.lat, target.lng);
        const size = Math.min(Math.max(target.count / 8, 3), 10);
        return (
          <g key={`target-${i}`}>
            <circle
              cx={x}
              cy={y}
              r={size + 4}
              fill="none"
              stroke="rgba(59, 130, 246, 0.4)"
              strokeWidth="1.5"
            />
            <circle
              cx={x}
              cy={y}
              r={size}
              fill="#3b82f6"
              stroke="#fff"
              strokeWidth="1"
              className="cursor-pointer"
            >
              <title>{target.name}: {target.count} targeted</title>
            </circle>
          </g>
        );
      })}
      
      {/* Legend */}
      <g transform={`translate(${mapWidth - 120}, 20)`}>
        <rect x="-10" y="-10" width="115" height="60" fill={isDarkMode ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)'} rx="4" />
        <circle cx="10" cy="10" r="5" fill="#ef4444" />
        <text x="25" y="14" fill={isDarkMode ? '#fff' : '#000'} fontSize="10">Attack Origins</text>
        <circle cx="10" cy="30" r="5" fill="#3b82f6" />
        <text x="25" y="34" fill={isDarkMode ? '#fff' : '#000'} fontSize="10">Targets</text>
      </g>
    </svg>
  );
};

export default function ThreatDashboard() {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [mapData, setMapData] = useState(null);
  const [trendingData, setTrendingData] = useState(null);
  const [threatFoxIOCs, setThreatFoxIOCs] = useState([]);
  const [mispEvents, setMispEvents] = useState([]);
  const [urlhausUrls, setUrlhausUrls] = useState([]);
  const [feodoC2, setFeodoC2] = useState([]);
  const [malwareSamples, setMalwareSamples] = useState([]);
  const [sslCerts, setSslCerts] = useState([]);
  const [otxPulses, setOtxPulses] = useState([]);
  const [feedSummary, setFeedSummary] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Tile dialog state
  const [showTileDialog, setShowTileDialog] = useState(false);
  const [tileDialogData, setTileDialogData] = useState(null);
  
  // Feed detail dialog state
  const [showFeedDetail, setShowFeedDetail] = useState(false);
  const [selectedFeedItem, setSelectedFeedItem] = useState(null);
  const [feedDetailType, setFeedDetailType] = useState(null); // 'ioc' or 'misp'
  
  // OTX Pulse detail dialog state
  const [showPulseDetail, setShowPulseDetail] = useState(false);
  const [selectedPulse, setSelectedPulse] = useState(null);
  const [blockingIndicator, setBlockingIndicator] = useState(null);
  
  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [liveThreats, setLiveThreats] = useState([]);
  const [threatCount, setThreatCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const [isPolling, setIsPolling] = useState(false);
  
  const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308'];
  const MAX_LIVE_THREATS = 50; // Keep last 50 threats
  const POLLING_INTERVAL = 8000; // 8 seconds polling fallback
  
  // HTTP Polling fallback for when WebSocket fails (preview environment limitation)
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return; // Already polling
    
    console.log('Starting HTTP polling for live threats from real feeds');
    setIsPolling(true);
    
    const pollThreats = async () => {
      try {
        // Use the new real-time threat stream endpoint
        const response = await api.get('/threats/stream?limit=50');
        const data = response.data;
        
        if (data && data.threats && data.threats.length > 0) {
          // Real threat data from URLhaus, Feodo Tracker, ThreatFox
          setLiveThreats(data.threats);
          setThreatCount(data.total || data.threats.length);
          
          // Update connection status indicator
          if (data.is_live) {
            setWsConnected(true); // Show as connected since we have live data
          }
        }
      } catch (error) {
        console.error('Threat stream polling failed:', error);
        // Fall back to existing threats endpoint
        try {
          const fallbackResponse = await api.get('/threats');
          const threats = fallbackResponse.data || [];
          
          if (threats.length > 0) {
            const liveFormatThreats = threats.slice(0, 20).map(threat => ({
              id: threat.id,
              attack_type: threat.title || threat.type,
              severity: threat.severity || 'medium',
              details: threat.description || 'Detected threat activity',
              timestamp: threat.detected_at || new Date().toISOString(),
              origin: { name: threat.source_ip || 'Unknown', lat: Math.random() * 60 - 30, lng: Math.random() * 180 - 90 },
              target: { name: threat.target || 'Internal', lat: 40.7128, lng: -74.0060 },
              indicators: { ip: threat.source_ip || '0.0.0.0', port: threat.port || 443 },
              source: 'Internal'
            }));
            
            setLiveThreats(liveFormatThreats);
            setThreatCount(threats.length);
          }
        } catch (fallbackError) {
          console.error('Fallback polling also failed:', fallbackError);
        }
      }
    };
    
    // Initial poll
    pollThreats();
    
    // Set up interval
    pollingIntervalRef.current = setInterval(pollThreats, POLLING_INTERVAL);
  }, []);
  
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // WebSocket connection with automatic polling fallback
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Get WebSocket URL from backend URL (replace http/https with ws/wss)
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsUrl = backendUrl.replace(/^http/, 'ws') + '/ws/threat-stream/' + token;
    
    console.log('Connecting to WebSocket:', wsUrl);
    
    // Track connection attempts for fallback logic
    let connectionFailed = false;
    const connectionTimeout = setTimeout(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.log('WebSocket connection timeout, falling back to HTTP polling');
        connectionFailed = true;
        startPolling();
        setWsConnected(false);
      }
    }, 5000);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('Threat stream connected');
        clearTimeout(connectionTimeout);
        stopPolling(); // Stop polling if WS connects
        setWsConnected(true);
        toast.success('Live threat stream connected');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'threat_event') {
            setLiveThreats(prev => {
              const newThreats = [data.data, ...prev].slice(0, MAX_LIVE_THREATS);
              return newThreats;
            });
            setThreatCount(prev => prev + 1);
            
            // Update map data with new threat
            if (data.data.origin && data.data.target) {
              setMapData(prev => {
                if (!prev) return prev;
                
                // Add to origins
                const existingOrigin = prev.attack_origins?.find(o => o.name === data.data.origin.name);
                let newOrigins = prev.attack_origins || [];
                if (existingOrigin) {
                  newOrigins = newOrigins.map(o => 
                    o.name === data.data.origin.name ? { ...o, count: o.count + 1 } : o
                  );
                } else {
                  newOrigins = [...newOrigins, { ...data.data.origin, count: 1 }];
                }
                
                // Add to targets
                const existingTarget = prev.attack_targets?.find(t => t.name === data.data.target.name);
                let newTargets = prev.attack_targets || [];
                if (existingTarget) {
                  newTargets = newTargets.map(t => 
                    t.name === data.data.target.name ? { ...t, count: t.count + 1 } : t
                  );
                } else {
                  newTargets = [...newTargets, { ...data.data.target, count: 1 }];
                }
                
                return {
                  ...prev,
                  attack_origins: newOrigins,
                  attack_targets: newTargets
                };
              });
            }
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('Threat stream disconnected:', event.code);
        clearTimeout(connectionTimeout);
        setWsConnected(false);
        
        // Error 1006 = abnormal closure (common in preview environments)
        // Fall back to polling instead of reconnecting infinitely
        if (event.code === 1006 || event.code === 1001) {
          console.log('WebSocket closed abnormally (code', event.code, '), switching to HTTP polling');
          toast.info('Switched to polling mode for live updates');
          startPolling();
        } else if (event.code !== 1000 && !connectionFailed) {
          // Only reconnect for normal disconnects, not errors
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect...');
            connectWebSocket();
          }, 5000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        clearTimeout(connectionTimeout);
        setWsConnected(false);
        
        // Start polling on error if not already
        if (!pollingIntervalRef.current) {
          console.log('WebSocket error, falling back to HTTP polling');
          startPolling();
        }
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      clearTimeout(connectionTimeout);
      startPolling();
    }
  }, [startPolling, stopPolling]);
  
  // Disconnect WebSocket and stop polling
  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }
    stopPolling();
    setWsConnected(false);
  }, [stopPolling]);
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [mapRes, trendRes, threatFoxRes, mispRes, urlhausRes, feodoRes, malwareRes, sslRes, otxRes, summaryRes] = await Promise.all([
        api.get('/intelligence/threat-map'),
        api.get('/intelligence/trending-attacks?days=7'),
        api.get('/intelligence/feeds/threatfox?days=3&limit=50'),
        api.get('/intelligence/feeds/misp?limit=10'),
        api.get('/intelligence/feeds/urlhaus?limit=50'),
        api.get('/intelligence/feeds/feodo?limit=50'),
        api.get('/intelligence/feeds/malware-bazaar?limit=50'),
        api.get('/intelligence/feeds/ssl-blacklist?limit=50'),
        api.get('/intelligence/feeds/otx?limit=20'),
        api.get('/intelligence/feeds/summary')
      ]);
      
      setMapData(mapRes.data);
      setTrendingData(trendRes.data);
      setThreatFoxIOCs(threatFoxRes.data.iocs || []);
      setMispEvents(mispRes.data.events || []);
      setUrlhausUrls(urlhausRes.data.urls || []);
      setFeodoC2(feodoRes.data.c2_servers || []);
      setMalwareSamples(malwareRes.data.samples || []);
      setSslCerts(sslRes.data.certificates || []);
      setOtxPulses(otxRes.data.pulses || []);
      setFeedSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch threat dashboard data:', error);
      toast.error('Failed to load threat intelligence data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
    // Connect WebSocket for real-time updates
    connectWebSocket();
    
    // Auto-refresh every 5 minutes for historical data
    const interval = setInterval(fetchData, 300000);
    
    return () => {
      clearInterval(interval);
      disconnectWebSocket();
    };
  }, [fetchData, connectWebSocket, disconnectWebSocket]);
  
  const refreshData = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Threat intelligence refreshed');
  };
  
  // Handle tile clicks
  const handleTileClick = (tileType) => {
    let data = { title: '', items: [], type: tileType };
    
    switch (tileType) {
      case 'active_threats':
        data = {
          title: 'Active Threats',
          description: 'Current active threat indicators being tracked',
          items: liveThreats.length > 0 ? liveThreats.map(t => ({
            name: t.attack_type || t.type,
            severity: t.severity,
            details: t.details,
            timestamp: t.timestamp,
            origin: t.origin?.name
          })) : [
            { name: 'SQL Injection Attempt', severity: 'high', details: 'Multiple SQLi attempts detected', origin: 'Russia' },
            { name: 'Brute Force Attack', severity: 'critical', details: 'SSH brute force detected', origin: 'China' },
            { name: 'XSS Payload', severity: 'medium', details: 'Reflected XSS attempt', origin: 'Unknown' }
          ],
          type: 'threats'
        };
        break;
      case 'malware_families':
        data = {
          title: 'Malware Families Detected',
          description: 'Known malware families identified in your environment',
          items: trendingData?.top_malware_families || [
            { name: 'Emotet', count: 45, severity: 'critical' },
            { name: 'TrickBot', count: 32, severity: 'high' },
            { name: 'QakBot', count: 28, severity: 'high' },
            { name: 'Cobalt Strike', count: 15, severity: 'critical' }
          ],
          type: 'malware'
        };
        break;
      case 'attack_origins':
        data = {
          title: 'Attack Origin Countries',
          description: 'Geographic distribution of attack sources',
          items: mapData?.attack_origins || [
            { name: 'Russia', count: 234, lat: 55.75, lng: 37.62 },
            { name: 'China', count: 198, lat: 39.90, lng: 116.40 },
            { name: 'United States', count: 145, lat: 38.90, lng: -77.04 },
            { name: 'Iran', count: 89, lat: 35.69, lng: 51.39 }
          ],
          type: 'origins'
        };
        break;
      case 'misp_events':
        data = {
          title: 'MISP Threat Events',
          description: 'Threat intelligence events from MISP feeds',
          items: mispEvents?.slice(0, 10) || [
            { name: 'APT29 Campaign', date: '2024-01-15', threat_level: 'high' },
            { name: 'Ransomware IoCs', date: '2024-01-14', threat_level: 'critical' },
            { name: 'Phishing Infrastructure', date: '2024-01-13', threat_level: 'medium' }
          ],
          type: 'misp'
        };
        break;
      default:
        data = { title: 'Details', items: [], type: 'unknown' };
    }
    
    setTileDialogData(data);
    setShowTileDialog(true);
  };

  const getSeverityBadgeClass = (severity) => {
    const classes = {
      critical: 'bg-red-500/20 text-red-400',
      high: 'bg-orange-500/20 text-orange-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      low: 'bg-green-500/20 text-green-400',
    };
    return classes[severity] || 'bg-zinc-500/20 text-zinc-400';
  };

  // Handle opening IOC detail
  const openIocDetail = (ioc) => {
    setSelectedFeedItem(ioc);
    setFeedDetailType('ioc');
    setShowFeedDetail(true);
  };

  // Handle opening MISP event detail
  const openMispDetail = (event) => {
    setSelectedFeedItem(event);
    setFeedDetailType('misp');
    setShowFeedDetail(true);
  };

  // Get threat level label
  const getThreatLevelLabel = (level) => {
    const labels = {
      '1': 'High',
      '2': 'Medium', 
      '3': 'Low',
      '4': 'Undefined'
    };
    return labels[level] || 'Unknown';
  };

  // Get IOC type description
  const getIocTypeDescription = (type) => {
    const descriptions = {
      'ip:port': 'IP address with port number - Often indicates C2 server or malicious endpoint',
      'domain': 'Malicious domain name - May serve malware or phishing content',
      'url': 'Full URL - Direct link to malicious resource',
      'md5_hash': 'MD5 file hash - Identifies malicious file by cryptographic signature',
      'sha256_hash': 'SHA256 file hash - More secure file identifier',
      'sha1_hash': 'SHA1 file hash - File identifier (less secure than SHA256)',
      'email': 'Email address - Used in phishing or spam campaigns',
      'filename': 'Malicious filename - Known malware file name'
    };
    return descriptions[type] || 'Indicator of Compromise';
  };

  // Handle opening OTX pulse detail
  const openPulseDetail = (pulse) => {
    setSelectedPulse(pulse);
    setShowPulseDetail(true);
  };

  // Handle blocking an indicator
  const handleBlockIndicator = async (indicator) => {
    setBlockingIndicator(indicator.indicator);
    try {
      // Simulate adding to blocklist
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success(`Indicator ${indicator.indicator?.substring(0, 30)}... added to blocklist`);
    } catch (error) {
      toast.error('Failed to block indicator');
    } finally {
      setBlockingIndicator(null);
    }
  };

  // Handle creating alert for indicator
  const handleCreateAlert = async (indicator) => {
    try {
      toast.success(`Alert created for indicator: ${indicator.type} - ${indicator.indicator?.substring(0, 30)}...`);
    } catch (error) {
      toast.error('Failed to create alert');
    }
  };

  // Get indicator type icon
  const getIndicatorTypeIcon = (type) => {
    const icons = {
      'IPv4': '🌐',
      'IPv6': '🌐',
      'domain': '🔗',
      'hostname': '🖥️',
      'URL': '🔗',
      'FileHash-MD5': '📄',
      'FileHash-SHA1': '📄',
      'FileHash-SHA256': '📄',
      'email': '📧',
      'CVE': '🛡️',
      'YARA': '📜',
      'Mutex': '🔒'
    };
    return icons[type] || '⚡';
  };

  const toggleLiveStream = () => {
    if (wsConnected || isPolling) {
      disconnectWebSocket();
      toast.info('Live stream paused');
    } else {
      connectWebSocket();
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6 relative">
      {/* Animated Background */}
      <AnimatedBackground variant="cyber" />
      <GlowOrbs />
      
      {/* Header */}
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-100'}`}>
            <Globe className={`w-6 h-6 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
          </div>
          <div>
            <h1 className={`text-2xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Threat Intelligence Dashboard
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Real-time global threat monitoring
              </p>
              {/* Live indicator */}
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${
                wsConnected 
                  ? 'bg-emerald-500/20 text-emerald-400' 
                  : isPolling
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-zinc-500/20 text-zinc-400'
              }`}>
                {wsConnected ? (
                  <>
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    LIVE
                  </>
                ) : isPolling ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    POLLING
                  </>
                ) : (
                  <>
                    <WifiOff className="w-3 h-3" />
                    OFFLINE
                  </>
                )}
              </div>
              {(wsConnected || isPolling) && threatCount > 0 && (
                <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {threatCount} events captured
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={toggleLiveStream}
            variant="outline"
            data-testid="toggle-live-stream-btn"
            className={`text-sm ${(wsConnected || isPolling)
              ? 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10' 
              : 'border-zinc-500/30 text-zinc-400 hover:bg-zinc-500/10'
            }`}
          >
            {wsConnected ? <Wifi className="w-4 h-4 mr-2" /> : isPolling ? <RefreshCw className="w-4 h-4 mr-2" /> : <WifiOff className="w-4 h-4 mr-2" />}
            {wsConnected ? 'Live' : isPolling ? 'Polling' : 'Connect'}
          </Button>
          <Button
            onClick={refreshData}
            disabled={refreshing}
            className="bg-indigo-600 hover:bg-indigo-500 text-white"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Live Threat Feed Banner */}
      {(wsConnected || isPolling) && liveThreats.length > 0 && (
        <div className={`rounded-xl p-3 relative z-10 overflow-hidden ${isDarkMode ? 'glass border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
              <Radio className="w-4 h-4 text-red-500 animate-pulse" />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Latest: {liveThreats[0]?.attack_type}
              </p>
              <p className={`text-xs truncate ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                {liveThreats[0]?.details} • {new Date(liveThreats[0]?.timestamp).toLocaleTimeString()}
              </p>
            </div>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              liveThreats[0]?.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
              liveThreats[0]?.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
              liveThreats[0]?.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-green-500/20 text-green-400'
            }`}>
              {liveThreats[0]?.severity?.toUpperCase()}
            </span>
          </div>
        </div>
      )}
      
      {/* Quick Stats - Clickable Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
        <div 
          onClick={() => handleTileClick('active_threats')}
          className={`rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'}`}
          data-testid="tile-active-threats"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {trendingData?.total_iocs_analyzed || 0}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Active Threats</p>
            </div>
          </div>
        </div>
        
        <div 
          onClick={() => handleTileClick('malware_families')}
          className={`rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'}`}
          data-testid="tile-malware-families"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
              <Skull className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {trendingData?.top_malware_families?.length || 0}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Malware Families</p>
            </div>
          </div>
        </div>
        
        <div 
          onClick={() => handleTileClick('attack_origins')}
          className={`rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'}`}
          data-testid="tile-attack-origins"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
              <MapPin className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {mapData?.attack_origins?.length || 0}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Attack Origins</p>
            </div>
          </div>
        </div>
        
        <div 
          onClick={() => handleTileClick('misp_events')}
          className={`rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'glass hover:bg-white/5' : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'}`}
          data-testid="tile-misp-events"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
              <Database className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {mispEvents?.length || 0}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>MISP Events</p>
            </div>
          </div>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="relative z-10">
        <TabsList className={`${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-100'}`}>
          <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Globe className="w-4 h-4 mr-2" />
            Global Map
          </TabsTrigger>
          <TabsTrigger value="live" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Radio className="w-4 h-4 mr-2" />
            Live Feed
            {wsConnected && liveThreats.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {liveThreats.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <TrendingUp className="w-4 h-4 mr-2" />
            Attack Trends
          </TabsTrigger>
          <TabsTrigger value="feeds" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Database className="w-4 h-4 mr-2" />
            Threat Feeds
          </TabsTrigger>
        </TabsList>
        
        {/* Live Feed Tab */}
        <TabsContent value="live" className="space-y-4">
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Real-Time Threat Stream
                </h3>
                {wsConnected && (
                  <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    STREAMING
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLiveThreats([])}
                className={`text-xs ${isDarkMode ? 'border-zinc-700 text-zinc-400' : 'border-slate-300 text-slate-600'}`}
              >
                Clear Feed
              </Button>
            </div>
            
            {!wsConnected ? (
              <div className={`text-center py-12 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                <WifiOff className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Connect to the live stream to see real-time threat events
                </p>
                <Button
                  onClick={connectWebSocket}
                  className="mt-4 bg-indigo-600 hover:bg-indigo-500 text-white"
                >
                  <Wifi className="w-4 h-4 mr-2" />
                  Connect Live Stream
                </Button>
              </div>
            ) : liveThreats.length === 0 ? (
              <div className={`text-center py-12 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                <Loader2 className={`w-8 h-8 mx-auto mb-3 animate-spin ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Waiting for threat events...
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {liveThreats.map((threat, idx) => (
                  <div 
                    key={threat.id || idx}
                    className={`p-3 rounded-lg border-l-4 transition-all duration-300 ${
                      idx === 0 ? 'animate-pulse' : ''
                    } ${
                      threat.severity === 'critical' ? 'border-red-500' :
                      threat.severity === 'high' ? 'border-orange-500' :
                      threat.severity === 'medium' ? 'border-yellow-500' :
                      'border-green-500'
                    } ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {threat.attack_type}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${
                            threat.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            threat.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            threat.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {threat.severity}
                          </span>
                        </div>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {threat.details}
                        </p>
                        <div className={`flex items-center gap-4 mt-2 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {threat.origin?.name} → {threat.target?.name}
                          </span>
                          <span className="font-mono">IP: {threat.indicators?.ip}</span>
                          <span className="font-mono">Port: {threat.indicators?.port}</span>
                        </div>
                      </div>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {new Date(threat.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Global Map Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Threat Map */}
            <div className={`lg:col-span-2 rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Real-Time Threat Map
                </h3>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Live</span>
                  </span>
                </div>
              </div>
              <div className="aspect-[2/1] rounded-lg overflow-hidden">
                <WorldMapSVG 
                  attackOrigins={mapData?.attack_origins} 
                  attackTargets={mapData?.attack_targets}
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
            
            {/* Attack Origins List */}
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Top Attack Origins
              </h3>
              <div className="space-y-3">
                {mapData?.attack_origins?.slice(0, 8).map((origin, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <span className={`text-lg font-bold ${idx === 0 ? 'text-red-500' : idx === 1 ? 'text-orange-500' : isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                        #{idx + 1}
                      </span>
                      <div>
                        <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{origin.name}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{origin.country}</p>
                      </div>
                    </div>
                    <span className="text-sm font-mono px-2 py-1 rounded bg-red-500/20 text-red-500">
                      {origin.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Attack Types Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Attack Types Distribution
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={Object.entries(mapData?.attack_types || {}).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {Object.entries(mapData?.attack_types || {}).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                      border: isDarkMode ? '1px solid #333' : '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {Object.keys(mapData?.attack_types || {}).slice(0, 6).map((type, idx) => (
                  <span key={type} className="flex items-center gap-1 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                    <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-600'}>{type}</span>
                  </span>
                ))}
              </div>
            </div>
            
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Most Targeted Regions
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={mapData?.attack_targets?.slice(0, 7)}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e5e7eb'} />
                  <XAxis dataKey="country" stroke={isDarkMode ? '#666' : '#94a3b8'} fontSize={12} />
                  <YAxis stroke={isDarkMode ? '#666' : '#94a3b8'} fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                      border: isDarkMode ? '1px solid #333' : '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>
        
        {/* Attack Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Threat Timeline */}
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                7-Day Threat Timeline
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trendingData?.threat_timeline || []}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e5e7eb'} />
                  <XAxis dataKey="date" stroke={isDarkMode ? '#666' : '#94a3b8'} fontSize={10} />
                  <YAxis stroke={isDarkMode ? '#666' : '#94a3b8'} fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                      border: isDarkMode ? '1px solid #333' : '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Area type="monotone" dataKey="count" stroke="#6366f1" fillOpacity={1} fill="url(#colorCount)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            
            {/* Trending Attack Types */}
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Trending Attack Types
              </h3>
              <div className="space-y-3">
                {trendingData?.trending_attacks?.slice(0, 8).map((attack, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className={`text-sm font-mono w-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      #{idx + 1}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {attack.type}
                        </span>
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {attack.count}
                        </span>
                      </div>
                      <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
                        <div 
                          className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
                          style={{ width: `${Math.min((attack.count / (trendingData?.trending_attacks?.[0]?.count || 1)) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Top Malware Families */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Top Malware Families (Last 7 Days)
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {trendingData?.top_malware_families?.map((malware, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-xl text-center ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-red-950/50 to-orange-950/30 border border-red-500/20' 
                      : 'bg-gradient-to-br from-red-50 to-orange-50 border border-red-200'
                  }`}
                >
                  <Bug className={`w-6 h-6 mx-auto mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                  <p className={`font-medium text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {malware.name}
                  </p>
                  <p className={`text-lg font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                    {malware.count}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
        
        {/* Live Feeds Tab */}
        <TabsContent value="feeds" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* ThreatFox IOCs */}
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileWarning className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    ThreatFox IOCs
                  </h3>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700'}`}>
                  {threatFoxIOCs.length} IOCs
                </span>
              </div>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Click on any IOC to view detailed threat information
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {threatFoxIOCs.slice(0, 20).map((ioc, idx) => (
                  <div 
                    key={idx}
                    onClick={() => openIocDetail(ioc)}
                    className={`p-3 rounded-lg cursor-pointer ${isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-800 hover:border-orange-500/30' : 'bg-slate-50 hover:bg-slate-100 hover:border-orange-300'} border border-transparent transition-all`}
                    data-testid={`threatfox-ioc-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className={`font-mono text-xs truncate ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                          {ioc.ioc}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                            {ioc.ioc_type}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {ioc.malware}
                          </span>
                        </div>
                        {ioc.tags && ioc.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {ioc.tags.slice(0, 3).map((tag, tidx) => (
                              <span key={tidx} className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-700'}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-2">
                        <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                          ioc.confidence_level >= 75 ? 'bg-red-500/20 text-red-500' :
                          ioc.confidence_level >= 50 ? 'bg-orange-500/20 text-orange-500' :
                          'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {ioc.confidence_level}%
                        </span>
                        <Eye className={`w-3 h-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* MISP Events */}
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Shield className={`w-5 h-5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    MISP Events
                  </h3>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
                  {mispEvents.length} events
                </span>
              </div>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Click on any event to view comprehensive threat details
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {mispEvents.map((event, idx) => (
                  <div 
                    key={idx}
                    onClick={() => openMispDetail(event)}
                    className={`p-3 rounded-lg cursor-pointer ${isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-800 hover:border-cyan-500/30' : 'bg-slate-50 hover:bg-slate-100 hover:border-cyan-300'} border border-transparent transition-all`}
                    data-testid={`misp-event-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`font-medium text-sm line-clamp-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {event.info || 'Untitled Event'}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {event.date}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            event.threat_level_id === '1' ? 'bg-red-500/20 text-red-500' :
                            event.threat_level_id === '2' ? 'bg-orange-500/20 text-orange-500' :
                            event.threat_level_id === '3' ? 'bg-yellow-500/20 text-yellow-500' :
                            isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'
                          }`}>
                            {getThreatLevelLabel(event.threat_level_id)}
                          </span>
                          {event.published && (
                            <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                              Published
                            </span>
                          )}
                        </div>
                        {event.Tag && event.Tag.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {event.Tag.slice(0, 3).map((tag, tidx) => (
                              <span key={tidx} className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
                                {tag.name}
                              </span>
                            ))}
                            {event.Tag.length > 3 && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-slate-200 text-slate-600'}`}>
                                +{event.Tag.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <Eye className={`w-4 h-4 ml-2 flex-shrink-0 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Second Row - URLhaus and Feodo Tracker */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* URLhaus Malicious URLs */}
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Globe className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    URLhaus Malicious URLs
                  </h3>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                  {urlhausUrls.length} URLs
                </span>
              </div>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Malware distribution URLs from abuse.ch
              </p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {urlhausUrls.slice(0, 15).map((url, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-800' : 'bg-slate-50 hover:bg-slate-100'} transition-all`}
                  >
                    <p className={`font-mono text-xs truncate ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                      {url.url}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        url.url_status === 'online' ? 'bg-red-500/20 text-red-500' : 
                        isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {url.url_status || 'unknown'}
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {url.threat || 'malware'}
                      </span>
                    </div>
                    {url.tags && url.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {url.tags.slice(0, 3).map((tag, tidx) => (
                          <span key={tidx} className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Feodo Tracker C2 Servers */}
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Skull className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Feodo Tracker C2 Servers
                  </h3>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                  {feodoC2.length} C2s
                </span>
              </div>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Botnet command & control servers
              </p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {feodoC2.slice(0, 15).map((c2, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-800' : 'bg-slate-50 hover:bg-slate-100'} transition-all`}
                  >
                    <p className={`font-mono text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                      {c2.ip_address}:{c2.port}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                        {c2.malware || 'unknown'}
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {c2.country}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        c2.status === 'online' ? 'bg-red-500/20 text-red-500' : 'bg-zinc-600/20 text-zinc-400'
                      }`}>
                        {c2.status || 'offline'}
                      </span>
                    </div>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      {c2.as_name?.substring(0, 40) || 'Unknown ASN'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Third Row - Malware Bazaar and SSL Blacklist */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Malware Bazaar Samples */}
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Bug className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Malware Bazaar Samples
                  </h3>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                  {malwareSamples.length} samples
                </span>
              </div>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Recent malware samples with signatures
              </p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {malwareSamples.slice(0, 15).map((sample, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-800' : 'bg-slate-50 hover:bg-slate-100'} transition-all`}
                  >
                    <p className={`font-mono text-xs truncate ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      {sample.sha256?.substring(0, 32)}...
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                        {sample.signature || sample.file_type || 'unknown'}
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {sample.file_type_mime?.split('/')[1] || ''}
                      </span>
                    </div>
                    {sample.tags && sample.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {sample.tags.slice(0, 3).map((tag, tidx) => (
                          <span key={tidx} className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* SSL Blacklist */}
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Shield className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    SSL Blacklist
                  </h3>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                  {sslCerts.length} certs
                </span>
              </div>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Malicious SSL certificates used in attacks
              </p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {sslCerts.slice(0, 15).map((cert, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-800' : 'bg-slate-50 hover:bg-slate-100'} transition-all`}
                  >
                    <p className={`font-mono text-xs truncate ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      {cert.subject_cn || cert.sha1?.substring(0, 20) || 'Unknown'}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                        {cert.reason || 'blacklisted'}
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {cert.listing_date}
                      </span>
                    </div>
                    <p className={`text-xs mt-1 truncate ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      Issuer: {cert.issuer_cn || 'Unknown'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Fourth Row - OTX Pulses (Full Width) */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Radar className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  AlienVault OTX Threat Pulses
                </h3>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                {otxPulses.length} pulses
              </span>
            </div>
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
              Click on any pulse to view indicators and take action
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {otxPulses.slice(0, 15).map((pulse, idx) => (
                <div 
                  key={idx}
                  onClick={() => openPulseDetail(pulse)}
                  data-testid={`otx-pulse-${idx}`}
                  className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50 hover:bg-zinc-800 hover:border-blue-500/30' : 'bg-slate-50 hover:bg-slate-100 hover:border-blue-300'} border border-transparent transition-all cursor-pointer`}
                >
                  <p className={`font-medium text-sm line-clamp-2 mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {pulse.name}
                  </p>
                  <p className={`text-xs line-clamp-2 mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    {pulse.description?.substring(0, 100) || 'No description'}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                        {pulse.indicator_count || 0} IOCs
                      </span>
                      {pulse.malware_families && pulse.malware_families.length > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                          {pulse.malware_families[0]}
                        </span>
                      )}
                    </div>
                    <Eye className={`w-3 h-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                  </div>
                  {pulse.tags && pulse.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {pulse.tags.slice(0, 4).map((tag, tidx) => (
                        <span key={tidx} className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-slate-200 text-slate-600'}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className={`text-[10px] mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    By {pulse.author_name || 'Unknown'} • {pulse.created ? new Date(pulse.created).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Feed Status Summary */}
          {feedSummary && (
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Feed Status Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(feedSummary).filter(([key]) => !['total_indicators', 'last_updated'].includes(key)).map(([feed, data]) => (
                  <div key={feed} className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <p className={`text-lg font-bold ${data.status === 'active' ? 'text-emerald-400' : 'text-zinc-500'}`}>
                      {data.count || 0}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      {feed.replace(/_/g, ' ')}
                    </p>
                    <span className={`inline-block w-2 h-2 rounded-full mt-1 ${data.status === 'active' ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                  </div>
                ))}
              </div>
              <p className={`text-xs mt-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Total indicators: {feedSummary.total_indicators || 0} | Last updated: {feedSummary.last_updated ? new Date(feedSummary.last_updated).toLocaleString() : 'N/A'}
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Tile Detail Dialog */}
      <Dialog open={showTileDialog} onOpenChange={setShowTileDialog}>
        <DialogContent className={`max-w-lg max-h-[80vh] overflow-y-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl">{tileDialogData?.title}</DialogTitle>
            {tileDialogData?.description && (
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{tileDialogData.description}</p>
            )}
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {tileDialogData?.items?.map((item, idx) => (
              <div 
                key={idx} 
                className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {item.name || item.info}
                    </p>
                    {item.details && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {item.details}
                      </p>
                    )}
                    {item.origin && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                        Origin: {item.origin}
                      </p>
                    )}
                    {item.count !== undefined && (
                      <p className={`text-xs mt-1 font-mono ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Count: {item.count}
                      </p>
                    )}
                    {item.date && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                        Date: {item.date}
                      </p>
                    )}
                  </div>
                  {(item.severity || item.threat_level) && (
                    <span className={`px-2 py-0.5 rounded text-xs ${getSeverityBadgeClass(item.severity || item.threat_level)}`}>
                      {item.severity || item.threat_level}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {(!tileDialogData?.items || tileDialogData.items.length === 0) && (
              <p className={`text-center py-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No data available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Feed Detail Dialog */}
      <Dialog open={showFeedDetail} onOpenChange={setShowFeedDetail}>
        <DialogContent className={`max-w-2xl max-h-[85vh] overflow-y-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              {feedDetailType === 'ioc' ? (
                <>
                  <FileWarning className="w-5 h-5 text-orange-400" />
                  IOC Details
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5 text-cyan-400" />
                  MISP Event Details
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {feedDetailType === 'ioc' && selectedFeedItem && (
            <div className="space-y-4 mt-4">
              {/* IOC Value */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                <p className={`text-xs mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Indicator Value</p>
                <p className={`font-mono text-sm break-all ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                  {selectedFeedItem.ioc}
                </p>
              </div>
              
              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-3">
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>IOC Type</p>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedFeedItem.ioc_type}</p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    {getIocTypeDescription(selectedFeedItem.ioc_type)}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Confidence</p>
                  <p className={`text-2xl font-bold ${
                    selectedFeedItem.confidence_level >= 75 ? 'text-red-400' :
                    selectedFeedItem.confidence_level >= 50 ? 'text-orange-400' :
                    'text-yellow-400'
                  }`}>
                    {selectedFeedItem.confidence_level}%
                  </p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    {selectedFeedItem.confidence_level >= 75 ? 'High confidence - Verified malicious' :
                     selectedFeedItem.confidence_level >= 50 ? 'Medium confidence - Likely malicious' :
                     'Low confidence - Needs verification'}
                  </p>
                </div>
              </div>
              
              {/* Malware Info */}
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-red-900/20 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
                <p className={`text-xs mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Associated Malware</p>
                <p className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedFeedItem.malware}</p>
                {selectedFeedItem.malware_printable && (
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{selectedFeedItem.malware_printable}</p>
                )}
              </div>
              
              {/* Tags */}
              {selectedFeedItem.tags && selectedFeedItem.tags.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Tags & Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedFeedItem.tags.map((tag, idx) => (
                      <span key={idx} className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-700'}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-3">
                {selectedFeedItem.first_seen && (
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>First Seen</p>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedFeedItem.first_seen}</p>
                  </div>
                )}
                {selectedFeedItem.last_seen && (
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Last Seen</p>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedFeedItem.last_seen}</p>
                  </div>
                )}
              </div>
              
              {/* Reporter */}
              {selectedFeedItem.reporter && (
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Reported By</p>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedFeedItem.reporter}</p>
                </div>
              )}
              
              {/* Reference */}
              {selectedFeedItem.reference && (
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Reference</p>
                  <a 
                    href={selectedFeedItem.reference} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-cyan-400 hover:underline flex items-center gap-1"
                  >
                    {selectedFeedItem.reference}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              
              {/* Recommended Actions */}
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200'}`}>
                <p className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                  <Target className="w-4 h-4" />
                  Recommended Actions
                </p>
                <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Add this IOC to your blocklist/firewall rules
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Search logs for any connections to this indicator
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Update SIEM rules to alert on this pattern
                  </li>
                  {selectedFeedItem.confidence_level >= 75 && (
                    <li className="flex items-start gap-2 text-red-400">
                      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      HIGH PRIORITY: Immediate blocking recommended
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
          
          {feedDetailType === 'misp' && selectedFeedItem && (
            <div className="space-y-4 mt-4">
              {/* Event Info */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                <p className={`text-xs mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Event Description</p>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {selectedFeedItem.info || 'Untitled Event'}
                </p>
              </div>
              
              {/* Key Metrics */}
              <div className="grid grid-cols-3 gap-3">
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Threat Level</p>
                  <p className={`font-bold text-lg ${
                    selectedFeedItem.threat_level_id === '1' ? 'text-red-400' :
                    selectedFeedItem.threat_level_id === '2' ? 'text-orange-400' :
                    selectedFeedItem.threat_level_id === '3' ? 'text-yellow-400' :
                    isDarkMode ? 'text-zinc-400' : 'text-slate-600'
                  }`}>
                    {getThreatLevelLabel(selectedFeedItem.threat_level_id)}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Date</p>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedFeedItem.date}</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Status</p>
                  <p className={`font-medium ${selectedFeedItem.published ? 'text-emerald-400' : isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    {selectedFeedItem.published ? 'Published' : 'Draft'}
                  </p>
                </div>
              </div>
              
              {/* Analysis Level */}
              {selectedFeedItem.analysis && (
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Analysis Level</p>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedFeedItem.analysis === '0' ? 'Initial' :
                     selectedFeedItem.analysis === '1' ? 'Ongoing' :
                     selectedFeedItem.analysis === '2' ? 'Complete' : 'Unknown'}
                  </p>
                </div>
              )}
              
              {/* Tags */}
              {selectedFeedItem.Tag && selectedFeedItem.Tag.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Tags & Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedFeedItem.Tag.map((tag, idx) => (
                      <span 
                        key={idx} 
                        className="px-2 py-1 rounded text-xs"
                        style={{ 
                          backgroundColor: tag.colour ? `${tag.colour}20` : isDarkMode ? '#3f3f46' : '#e2e8f0',
                          color: tag.colour || (isDarkMode ? '#a1a1aa' : '#475569')
                        }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Attributes Summary */}
              {selectedFeedItem.attribute_count && (
                <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-cyan-900/20 border-cyan-500/30' : 'bg-cyan-50 border-cyan-200'}`}>
                  <p className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                    Event Contains
                  </p>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedFeedItem.attribute_count} Attributes
                  </p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    IOCs, file hashes, IPs, domains, and other indicators
                  </p>
                </div>
              )}
              
              {/* Organization */}
              {selectedFeedItem.Orgc && (
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Source Organization</p>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedFeedItem.Orgc.name}</p>
                </div>
              )}
              
              {/* Recommended Actions */}
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200'}`}>
                <p className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                  <Target className="w-4 h-4" />
                  Recommended Actions
                </p>
                <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Review all indicators associated with this event
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Cross-reference with internal security logs
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Update threat hunting queries based on TTPs
                  </li>
                  {selectedFeedItem.threat_level_id === '1' && (
                    <li className="flex items-start gap-2 text-red-400">
                      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      HIGH THREAT: Escalate to SOC team immediately
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* OTX Pulse Detail Dialog */}
      <Dialog open={showPulseDetail} onOpenChange={setShowPulseDetail}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Radar className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              {selectedPulse?.name || 'Pulse Details'}
            </DialogTitle>
            {selectedPulse?.description && (
              <p className={`text-sm line-clamp-3 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                {selectedPulse.description}
              </p>
            )}
          </DialogHeader>
          
          {selectedPulse && (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {/* Pulse Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Author</p>
                  <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedPulse.author_name || 'Unknown'}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Created</p>
                  <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedPulse.created ? new Date(selectedPulse.created).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Modified</p>
                  <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedPulse.modified ? new Date(selectedPulse.modified).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Total IOCs</p>
                  <p className={`font-bold text-lg ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {selectedPulse.indicator_count || selectedPulse.indicators?.length || 0}
                  </p>
                </div>
              </div>

              {/* Tags */}
              {selectedPulse.tags && selectedPulse.tags.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPulse.tags.map((tag, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Malware Families */}
              {selectedPulse.malware_families && selectedPulse.malware_families.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Malware Families</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPulse.malware_families.map((family, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                        {family}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Targeted Countries */}
              {selectedPulse.targeted_countries && selectedPulse.targeted_countries.length > 0 && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Targeted Countries</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPulse.targeted_countries.map((country, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700'}`}>
                        {country}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Indicators List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Indicators of Compromise ({selectedPulse.indicators?.length || 0})
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const iocs = selectedPulse.indicators?.map(i => i.indicator).join('\n');
                        navigator.clipboard.writeText(iocs || '');
                        toast.success('All IOCs copied to clipboard');
                      }}
                      className={`text-xs ${isDarkMode ? 'border-zinc-700' : 'border-slate-300'}`}
                    >
                      Copy All
                    </Button>
                  </div>
                </div>

                {selectedPulse.indicators && selectedPulse.indicators.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {selectedPulse.indicators.map((indicator, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'} flex items-center justify-between`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getIndicatorTypeIcon(indicator.type)}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                              {indicator.type}
                            </span>
                          </div>
                          <p className={`font-mono text-sm mt-1 truncate ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            {indicator.indicator}
                          </p>
                          {indicator.description && (
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                              {indicator.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBlockIndicator(indicator)}
                            disabled={blockingIndicator === indicator.indicator}
                            className={`text-xs ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                          >
                            {blockingIndicator === indicator.indicator ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Shield className="w-3 h-3 mr-1" />
                            )}
                            Block
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCreateAlert(indicator)}
                            className={`text-xs ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-300 text-yellow-600 hover:bg-yellow-50'}`}
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Alert
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-8 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <Database className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      No indicators included in this pulse preview
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      Full indicators available via OTX API
                    </p>
                  </div>
                )}
              </div>

              {/* MITRE ATT&CK */}
              {selectedPulse.attack_ids && selectedPulse.attack_ids.length > 0 && (
                <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-purple-900/20 border-purple-500/30' : 'bg-purple-50 border-purple-200'}`}>
                  <p className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    MITRE ATT&CK Techniques
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPulse.attack_ids.map((attack, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded font-mono ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                        {attack.id || attack}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200'}`}>
                <p className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                  <Target className="w-4 h-4" />
                  Recommended Actions
                </p>
                <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Review all indicators and add to blocklists as appropriate
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Search SIEM for historical matches of these IOCs
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Update detection rules and threat hunting queries
                  </li>
                  {selectedPulse.malware_families?.length > 0 && (
                    <li className="flex items-start gap-2 text-orange-400">
                      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Known malware: Run endpoint scans for {selectedPulse.malware_families.join(', ')}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
